// "General details" card of the aircraft form: operator, tail, model, category, seats, years, status, photos.
import type { FormInstance } from 'antd';
import type { UseQueryResult } from '@tanstack/react-query';
import { Card, Checkbox, Col, Divider, Form, Input, Row, Select, Space } from 'antd';

import { listOperators } from '../../api/endpoints';
import { AIRCRAFT_CATEGORIES, CATEGORY_LABELS, type AircraftCategory } from '../../api/types';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../../components/NumberInput';
import { LIMITS } from '../../data/limits';
import type { Photo } from './aircraftFormRows';
import { PhotoUploader } from './PhotoUploader';

export function GeneralDetailsCard({
  form,
  operators,
  mainPhotoUrl,
  setMainPhotoUrl,
  photos,
  setPhotos,
}: {
  form: FormInstance;
  operators: UseQueryResult<Awaited<ReturnType<typeof listOperators>>>;
  mainPhotoUrl: string | null;
  setMainPhotoUrl: React.Dispatch<React.SetStateAction<string | null>>;
  photos: Photo[];
  setPhotos: React.Dispatch<React.SetStateAction<Photo[]>>;
}) {
  return (
    <Card title="General details" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="operatorId"
            label="Operator"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={operators.isLoading}
              options={(operators.data?.items || []).map((o) => ({
                value: o.id,
                label: o.name,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="tailNumber"
            label="Tail number"
            rules={[
              { required: true, message: 'Required' },
              { pattern: /^[A-Z0-9-]+$/, message: 'Uppercase letters, digits, dashes' },
            ]}
          >
            <Input maxLength={LIMITS.tailNumber}
              placeholder="G-ALPN1"
              onChange={(e) => form.setFieldValue('tailNumber', e.target.value.toUpperCase())}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="modelName"
            label="Model / name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input maxLength={LIMITS.aircraftModel} placeholder="Embraer Phenom 300" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select<AircraftCategory>
              options={AIRCRAFT_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="seatingCapacity"
            label="Seating capacity"
            rules={[{ required: true, message: 'Required' }]}
          >
            <NumberInput min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="yearOfManufacture" label="Year of manufacture">
            <NumberInput min={1960} max={2099} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="yearOfRefurbishment" label="Year of interior refurb.">
            <NumberInput min={1960} max={2099} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        {/* SHAR-197 — optional Owner's approval flag. */}
        <Col xs={24} md={8}>
          <Form.Item name="ownersApproval" label="Owner's approval" valuePropName="checked">
            <Checkbox>Owner's approval received</Checkbox>
          </Form.Item>
        </Col>
        <Col xs={24} md={6}>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider style={{ marginTop: 0 }}>Photos</Divider>
      <Space direction="vertical" style={{ width: '100%' }}>
        <PhotoUploader
          label="Main photo"
          urls={mainPhotoUrl ? [mainPhotoUrl] : []}
          onAdd={(url) => setMainPhotoUrl(url)}
          onRemove={() => setMainPhotoUrl(null)}
          maxCount={1}
        />
        <PhotoUploader
          label="Additional photos"
          urls={photos.map((p) => p.url)}
          onAdd={(url) => setPhotos((p) => [...p, { url }])}
          onRemove={(url) => setPhotos((p) => p.filter((x) => x.url !== url))}
          maxCount={12}
        />
      </Space>
    </Card>
  );
}
